import React, { useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { PagesDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { slugify, buildFullSlug } from '../../utils/slugify';
import { buildFullUrl } from '../../modules/infoHub';
import { usePagesApi } from '../../hooks/usePagesApi';
import { UNLAYER_PROJECT_ID, BASE_URL } from '../../config/config';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface PagesFormProps {
  initialValues: PagesDTO;
  onSubmit: (data: PagesDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: { id: 'basic-info', title: 'Basic Information' },
  content: { id: 'content', title: 'Content' },
  settings: { id: 'settings', title: 'Settings' },
} as const;

const normId = (g?: string | null) => (g || '').toLowerCase();

const PagesForm: React.FC<PagesFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<PagesDTO>(initialValues);
  const [pages, setPages] = useState<PagesDTO[]>([]);
  const emailEditorRef = useRef<EditorRef>(null);
  const { search } = usePagesApi();

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
  };

  const { errors, validate } = useFormValidation<PagesDTO>(validationRules);

  // Load all pages once — used both for the parent dropdown and to resolve the
  // parent's full slug when building this page's path.
  useEffect(() => {
    const loadPages = async () => {
      const response = await search({
        filters: {},
        page: 1,
        pageSize: 500,
        sortBy: 'Title',
        sortDescending: false,
      });
      setPages(response?.data?.results || []);
    };
    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smart URLs: the Title URL (slug) is derived from the title, and the Path URL
  // (full slug) is the parent's path + this slug. Both are read-only for the user.
  useEffect(() => {
    const slug = slugify(formData.title || '');
    let fullSlug = slug;

    if (formData.parentPageId) {
      const parent = pages.find(
        (p) => normId(p.id) === normId(formData.parentPageId)
      );
      if (!parent) return; // parent list not loaded yet — don't clobber existing values
      fullSlug = buildFullSlug(parent.slugFull, slug);
    }

    setFormData((prev) =>
      prev.slug === slug && prev.slugFull === fullSlug
        ? prev
        : { ...prev, slug, slugFull: fullSlug }
    );
  }, [formData.title, formData.parentPageId, pages]);

  const parentOptions = [
    { value: '', label: '(None — top level)' },
    ...pages
      .filter((p) => normId(p.id) !== normId(initialValues.id))
      .map((p) => ({ value: p.id, label: p.title })),
  ];

  // Load the saved Unlayer design (page.body) into the editor once it is ready.
  const onEditorReady: EmailEditorProps['onReady'] = (unlayer) => {
    if (initialValues.body) {
      try {
        unlayer.loadDesign(JSON.parse(initialValues.body));
      } catch (e) {
        console.error('Failed to parse saved page design JSON:', e);
      }
    }
  };

  const handleSave = () => {
    if (!validate(formData)) {
      return;
    }

    const editor = emailEditorRef.current?.editor;
    if (!editor) {
      onSubmit(formData);
      return;
    }

    // Export both the rendered HTML (uploaded to a file server-side via `content`)
    // and the design JSON (stored inline in `body` for re-editing via loadDesign).
    editor.exportHtml((data) => {
      const { design, html } = data;
      onSubmit({
        ...formData,
        content: html,
        body: JSON.stringify(design),
      });
    });
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="parentPageId"
          label="Parent Page"
          type="dropdown"
          value={formData.parentPageId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          options={parentOptions}
          searchable={true}
          placeholder="Select a parent page (optional)"
          helper="Leave as top level for a root page."
        />
        <FormField
          id="slug"
          label="Title URL"
          type="text"
          value={formData.slug || ''}
          onChange={() => {}}
          disabled={true}
          helper="Automatically generated from the title."
        />
        <FormField
          id="slugFull"
          label="Path URL"
          type="text"
          value={buildFullUrl(BASE_URL, formData.slugFull)}
          onChange={() => {}}
          disabled={true}
          helper="Automatically generated from the parent page and title URL."
        />
        <FormField
          id="position"
          label="Page Position"
          type="number"
          value={formData.position ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Position"
          helper="Ordering among sibling pages (lower shows first)."
        />
        <FormField
          id="description"
          label="Description"
          type="textarea"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter a short description"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.content.id}
        title={FORM_SECTIONS.content.title}
        columns={1}
      >
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Design the page below. On save, the rendered HTML is stored as a file and the
          design is saved so it can be edited again later.
        </p>
        <div className="w-full">
          <EmailEditor
            ref={emailEditorRef}
            onReady={onEditorReady}
            minHeight="700px"
            style={{ width: '100%' }}
            options={{
              projectId: UNLAYER_PROJECT_ID,
              displayMode: 'web',
            }}
          />
        </div>
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="isVisible"
          label="Visible in navigation"
          type="checkbox"
          value={formData.isVisible || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to show this page in navigation."
        />
        <FormField
          id="isRestricted"
          label="Restricted"
          type="checkbox"
          value={formData.isRestricted || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to restrict this page to authorised members."
        />
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make this page active."
        />
      </FormSection>
    </Form>
  );
};

export default PagesForm;
